import { IS_PRODUCTION } from '@/config/domains';
import { isDiagnosticsDomain } from '@/utils/runtimeNoisePolicy';

/**
 * Feature flags configuration with remote override support
 * 
 * Reads VITE_* env defaults and merges with per-hostname overrides from Supabase.
 * Remote overrides allow instant flag toggling without redeploys.
 * 
 * IMPORTANT: Always provides sync defaults for initial render (non-blocking).
 * Remote overrides are fetched asynchronously and merged afterward.
 */

export type FeatureFlag =
  | 'REMIXES_ENABLED'
  | 'SNAPLINKS_ENABLED'
  | 'SNAPPAGES_ENABLED'
  | 'PDF_ENABLED'
  | 'AI_ENABLED'
  | 'SNAPREPORTS_ENABLED'
  | 'SNAPDOCS_ENABLED'
  | 'CANVA_ENABLED'
  | 'GSHEETS_ENABLED';

export type FeatureFlags = {
  REMIXES_ENABLED: boolean;
  SNAPLINKS_ENABLED: boolean;
  SNAPPAGES_ENABLED: boolean;
  PDF_ENABLED: boolean;
  AI_ENABLED: boolean;
  SNAPREPORTS_ENABLED: boolean;
  SNAPDOCS_ENABLED: boolean;
  CANVA_ENABLED: boolean;
  GSHEETS_ENABLED: boolean;
};

const CORE_SNAPLINK_HOSTS = new Set(['snaplayer.app', 'www.snaplayer.app', 'dev.snaplayer.app']);

function shouldForceSnaplinksEnabledOnCoreHost(): boolean {
  if (typeof window === 'undefined') return false;
  return CORE_SNAPLINK_HOSTS.has(window.location.hostname.toLowerCase());
}

function coerceFlagValue(remoteValue: unknown, fallback: boolean): boolean {
  if (typeof remoteValue === 'boolean') return remoteValue;
  if (typeof remoteValue === 'string') {
    const normalized = remoteValue.trim().toLowerCase();
    if (normalized === 'true') return true;
    if (normalized === 'false') return false;
  }
  if (typeof remoteValue === 'number') {
    if (remoteValue === 1) return true;
    if (remoteValue === 0) return false;
  }
  return fallback;
}

// Default values from VITE_* environment variables (sync, always available)
export const getDefaultFlags = (): FeatureFlags => ({
  REMIXES_ENABLED: import.meta.env.VITE_FEATURE_REMIXES === 'true',
  // Keep SnapLinks available on primary app hosts even if env flags drift.
  SNAPLINKS_ENABLED: shouldForceSnaplinksEnabledOnCoreHost() || import.meta.env.VITE_FEATURE_SNAPLINKS === 'true',
  SNAPPAGES_ENABLED: import.meta.env.VITE_FEATURE_SNAPPAGES === 'true',
  // Keep PDF available in non-production environments while it remains gated on prod.
  PDF_ENABLED: !IS_PRODUCTION || import.meta.env.VITE_FEATURE_PDF === 'true',
  // AI: dev-only until integrations are production-ready.
  AI_ENABLED: !IS_PRODUCTION || import.meta.env.VITE_FEATURE_AI === 'true',
  // SnapReports & SnapDocs: dev-only until the simplified dashboard panels are production-ready.
  SNAPREPORTS_ENABLED: !IS_PRODUCTION || import.meta.env.VITE_FEATURE_SNAPREPORTS === 'true',
  SNAPDOCS_ENABLED: !IS_PRODUCTION || import.meta.env.VITE_FEATURE_SNAPDOCS === 'true',
  // Canva stays gated on production while launch approval work is still pending.
  CANVA_ENABLED: !IS_PRODUCTION || import.meta.env.VITE_FEATURE_CANVA === 'true',
  // Google Sheets is available by default, but can still be disabled explicitly per environment.
  GSHEETS_ENABLED: import.meta.env.VITE_FEATURE_GSHEETS !== 'false',
});

const defaults = getDefaultFlags();
const REMOTE_FLAGS_TIMEOUT_MS = 4000;

// Cache for flags (refreshed on page load or explicit reset)
let cached: FeatureFlags | null = null;

/**
 * Get feature flags for the current hostname
 * Merges VITE_* defaults with remote overrides from Supabase
 */
export async function getFeatureFlags(): Promise<FeatureFlags> {
  // Return cached if available
  if (cached) return cached;

  // Use defaults if not in browser
  if (typeof window === 'undefined') {
    cached = defaults;
    return cached;
  }

  // Normalize hostname: strip www. prefix so www.snaplayer.app and snaplayer.app use same flags
  let hostname = window.location.hostname;
  if (hostname.startsWith('www.')) {
    hostname = hostname.substring(4);
  }

  const allowDiagnosticsRemote = import.meta.env.VITE_FEATURE_REMOTE_OVERRIDES_ON_DIAGNOSTICS === 'true';
  if (!allowDiagnosticsRemote && isDiagnosticsDomain(hostname)) {
    cached = defaults;
    return cached;
  }
  
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  // Allow env to disable remote overrides entirely (useful for beta)
  const remoteEnabled = import.meta.env.VITE_FEATURE_REMOTE_OVERRIDES !== 'false';
  if (!remoteEnabled) {
    cached = defaults;
    return cached;
  }

  // Fallback to defaults if Supabase not configured
  if (!supabaseUrl || !supabaseAnonKey) {
    cached = defaults;
    return cached;
  }

  try {
    // Fetch remote override for this hostname (normalized, without www.)
    const url = `${supabaseUrl.replace(/\/$/, '')}/rest/v1/app_feature_flags?hostname=eq.${encodeURIComponent(hostname)}&select=flags`;
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), REMOTE_FLAGS_TIMEOUT_MS);
    const resp = await fetch(url, {
      // Avoid any intermediary/proxy caches returning stale flag JSON
      cache: 'no-store',
      signal: controller.signal,
      headers: {
        apikey: supabaseAnonKey,
        Authorization: `Bearer ${supabaseAnonKey}`,
        'Cache-Control': 'no-store',
        Pragma: 'no-cache',
      },
    }).finally(() => window.clearTimeout(timeoutId));

    if (!resp.ok) {
      throw new Error(`Flags fetch failed: ${resp.status}`);
    }

    const rows = await resp.json();
    const remote = rows?.[0]?.flags || {};

    // Merge: remote override takes precedence, fallback to defaults
    cached = {
      REMIXES_ENABLED: coerceFlagValue(remote.REMIXES_ENABLED, defaults.REMIXES_ENABLED),
      SNAPLINKS_ENABLED: coerceFlagValue(remote.SNAPLINKS_ENABLED, defaults.SNAPLINKS_ENABLED),
      SNAPPAGES_ENABLED: coerceFlagValue(remote.SNAPPAGES_ENABLED, defaults.SNAPPAGES_ENABLED),
      PDF_ENABLED: coerceFlagValue(remote.PDF_ENABLED, defaults.PDF_ENABLED),
      AI_ENABLED: coerceFlagValue(remote.AI_ENABLED, defaults.AI_ENABLED),
      SNAPREPORTS_ENABLED: coerceFlagValue(remote.SNAPREPORTS_ENABLED, defaults.SNAPREPORTS_ENABLED),
      SNAPDOCS_ENABLED: coerceFlagValue(remote.SNAPDOCS_ENABLED, defaults.SNAPDOCS_ENABLED),
      CANVA_ENABLED: coerceFlagValue(remote.CANVA_ENABLED, defaults.CANVA_ENABLED),
      GSHEETS_ENABLED: coerceFlagValue(remote.GSHEETS_ENABLED, defaults.GSHEETS_ENABLED),
    };

    return cached;
  } catch (error) {
    const isDev = import.meta.env.DEV;
    if (isDev || (typeof window !== 'undefined' && (window as any).SNAP_DEBUG)) {
      console.debug('Feature flags: using defaults (remote fetch failed):', error);
    }
    cached = defaults;
    return cached;
  }
}

/**
 * Reset the flags cache (useful after updating flags remotely)
 */
export function resetFeatureFlagsCache(): void {
  cached = null;
}

/**
 * Check if a specific feature flag is enabled
 * Convenience wrapper around getFeatureFlags()
 */
export async function isFeatureEnabled(flag: FeatureFlag): Promise<boolean> {
  const flags = await getFeatureFlags();
  return flags[flag];
}
